import type { FieldGroup, FormField } from '@/types/formSchema';

/**
 * Check if a field is conditionally required based on current form data.
 * Supports both new requiredWhenValues (array) and deprecated requiredWhenValue (string).
 */
function isConditionallyRequired(field: FormField, formData: Record<string, unknown>): boolean {
  if (!field.requiredWhenField) return false;
  const val = String(formData[field.requiredWhenField] ?? '');
  if (field.requiredWhenValues?.length) return field.requiredWhenValues.includes(val);
  if (field.requiredWhenValue !== undefined) return val === field.requiredWhenValue;
  // No value/values specified → required when the referenced field is non-empty
  return val !== '';
}

/**
 * Compute required fields directly from the API-provided FieldGroups and current form data.
 *
 * A field is required when:
 * 1. field.validation.required === true (base required, set by the API)
 * 2. field matches a conditional required rule (requiredWhenField + requiredWhenValues)
 *
 * Validation is driven exclusively by the /schema API response.
 */
export function getRequiredFieldsFromGroups(
  fieldGroups: FieldGroup[],
  formData: Record<string, unknown>,
): Set<string> {
  const required = new Set<string>();

  for (const group of fieldGroups) {
    for (const field of group.fields) {
      if (field.validation?.required || isConditionallyRequired(field, formData)) {
        required.add(field.id);
      }
    }
  }

  return required;
}

/**
 * Applies required field information to FieldGroups.
 *
 * Uses inline requiredWhenField/requiredWhenValue on each field (from the /schema API)
 * to determine conditional requirements.
 *
 * The function is generic to preserve any additional properties on the FieldGroup
 * (e.g., `summary` in FieldGroupAccordion).
 */
export function applyRequiredFieldsToGroups<T extends FieldGroup>(
  fieldGroups: T[],
  formData: Record<string, unknown>,
): T[] {
  const requiredFields = getRequiredFieldsFromGroups(fieldGroups, formData ?? {});

  return fieldGroups.map(group => ({
    ...group,
    fields: group.fields.map(
      (field): FormField => ({
        ...field,
        validation: {
          ...field.validation,
          required: requiredFields.has(field.id),
        },
      }),
    ),
  })) as T[];
}
